import { EventEmitter, Output, type OnInit } from '@angular/core';

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { Actions } from '@ngrx/effects';
import { BsModalService, type BsModalRef } from 'ngx-bootstrap/modal';

import { NpsModalComponent, type NPSFeedback } from '@ui/sp-nps/nps-modal.component';
import { NpsHttpService } from '@ui/sp-nps/nps.http.service';
import { isUuid } from '@common/helpers';

import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TranslateModule } from '@ngx-translate/core';

export type NPS = {
    show: (callback?: (wasShown?: boolean) => any) => BsModalRef<NpsModalComponent> | undefined;
    getSessionNumber: () => NPSFeedback['sessionNumber'];
    updateSessionNumber: (sessionNumber?: number | undefined) => void;
    setPublishTime: (date: Date | string) => void;
};

@Component({
    selector: 'sp-nps-loader',
    template: `<button *ngIf="!isAiAssistant" class="nps-btn btn btn-sm btn-action" (click)="showNpsModal(false)">
            <span class="sp-icon icon-star-fill"> </span> {{ 'nps_button_tooltip' | translate }}
        </button>
        <small
            *ngIf="isAiAssistant"
            class="sp-icon icon-star text-muted"
            [tooltip]="'ai_chat_feedback_btn_tooltip' | translate"
            [tooltipAppendToBody]="true"
            (click)="showNpsModal(true)"
        >
        </small>`,
    styles: [
        `
            ::ng-deep .modal-nps {
                width: 460px;
            }

            .nps-btn {
                position: fixed;
                bottom: -2px;
                right: 24px;
                z-index: 99;
                box-shadow: 2px 4px 8px rgba(32, 32, 35, 0.1);
                border-bottom-left-radius: 0;
                border-bottom-right-radius: 0;
                transition: bottom 0.2s ease-in-out;

                .sp-icon {
                    color: #f0ad4e;
                }

                &:hover {
                    bottom: 0;
                }
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, TooltipModule, TranslateModule],
})
export class SpNpsComponent implements OnInit {
    @Input() type: 'web' | 'mlp' = 'web';
    @Input() ready: boolean = false;
    @Input() isDirty: boolean = false;
    @Input() isAiAssistant: boolean = false;
    @Input() pageId!: number | string;

    @Output() init: EventEmitter<NPS> = new EventEmitter();

    actions$: Actions = inject(Actions);
    bsModalService: BsModalService = inject(BsModalService);
    npsHttpService: NpsHttpService = inject(NpsHttpService);

    storageKey = `${this.type}-nps`;
    sessionNumber: number = 0;
    npsIsOpen = false;

    sessionsViews: Record<number, boolean> = {
        1: false,
        2: false,
        3: false,
    };

    private getNpsSettings() {
        let npsSettings: any = localStorage.getItem(`${this.storageKey}`) ?? '{}';
        npsSettings = JSON.parse(npsSettings);

        return npsSettings;
    }
    private patchNpsSettings(patchObject: object) {
        const npsSettings = this.getNpsSettings();
        localStorage.setItem(`${this.storageKey}`, JSON.stringify({ ...npsSettings, ...patchObject }));
    }

    private getSessionNumber() {
        return this.sessionNumber;
    }

    private updateSessionNumber(sessionNumber?: number | undefined) {
        // implement manual change only for first session
        if (typeof sessionNumber === 'number' && sessionNumber === 1) {
            if (this.sessionsViews[1]) {
                return;
            }
            this.patchNpsSettings({ closedSessions: { 1: false, 2: false, 3: false } });
            this.sessionNumber = 1;
            this.sessionsViews[sessionNumber] = false;
            return;
        }

        if (this.sessionNumber === 1) {
            const npsSettings = this.getNpsSettings();
            this.patchNpsSettings({ closedSessions: { ...npsSettings.closedSessions, [this.sessionNumber]: true } });
            this.sessionNumber = 2;
            this.sessionsViews[this.sessionNumber] = true;
        } else if (this.sessionNumber === 2 && this.sessionsViews[2]) {
            this.sessionNumber = 0;
        }
    }

    ngOnInit(): void {
        this.npsHttpService.getNpsData().subscribe((npsViews) => {
            let npsSettings = this.getNpsSettings();
            const firstPublishedTime = npsSettings?.firstPublishedTime;

            npsViews.forEach((npsView) => {
                this.sessionsViews[npsView.sessionNumber] = true;
            });

            if (Object.keys(npsSettings).length === 0) {
                let firstConstructorVisit = new Date().getTime();

                if (this.sessionsViews[1] && !this.sessionsViews[2]) {
                    const createdAt = npsViews.find((npsView) => npsView.sessionNumber === 1)!.createdAt;
                    firstConstructorVisit = new Date(createdAt).getTime();
                }

                npsSettings = {
                    firstConstructorVisit,
                    firstPublishedTime,
                    // closed nps windows or session was ended
                    closedSessions: {
                        1: false,
                        2: false,
                        3: false,
                    },
                };
            }

            this.patchNpsSettings(npsSettings);

            Object.keys(npsSettings.closedSessions).forEach((key) => {
                if (!npsSettings.closedSessions[key]) {
                    return;
                }

                this.sessionsViews[Number(key)] = true;
            });

            const currentSession = Number(
                Object.keys(this.sessionsViews).find((key) => this.sessionsViews[Number(key)] === false)
            );

            if (!currentSession) {
                this.sessionNumber = 0;
            } else if (currentSession === 1 && !firstPublishedTime) {
                this.sessionNumber = 1;
            } else if (
                currentSession === 2 &&
                new Date().getTime() - npsSettings.firstConstructorVisit > 1000 * 60 * 60 * 24 * 3
            ) {
                this.sessionNumber = 2;
            } else {
                this.sessionNumber = 0;
            }

            (window as any).resetNpsSettings = () => {
                localStorage.removeItem(`${this.storageKey}`);
            };
            this.init.emit({
                show: this.show.bind(this),
                getSessionNumber: this.getSessionNumber.bind(this),
                updateSessionNumber: this.updateSessionNumber.bind(this),
                setPublishTime: this.setPublishTime.bind(this),
            });
        });
    }

    show(
        callback: (wasShown?: boolean) => any = () => {},
        fromButton?: boolean,
        fromAiAssistant?: boolean
    ): BsModalRef<NpsModalComponent> | undefined {
        // let IsDirtyChecking = this.sessionNumber === 1 ? !this.isDirty : false;
        const zeroSession = this.sessionNumber === 0;
        const alreadyViewed = this.sessionsViews[this.sessionNumber];

        // if (!this.ready || this.npsIsOpen || zeroSession || alreadyViewed || IsDirtyChecking) {
        if ((!this.ready || this.npsIsOpen || zeroSession || alreadyViewed) && !fromButton && !fromAiAssistant) {
            callback(false);
            return;
        }

        const modalRef = this.bsModalService.show(NpsModalComponent, {
            class: 'modal-nps',
            ignoreBackdropClick: true,
            initialState: { isAiAssistant: fromAiAssistant },
        });
        this.npsIsOpen = true;
        callback(true);
        modalRef.content!.onClose$.subscribe((feedback: NPSFeedback | false) => {
            this.npsIsOpen = false;

            if (!feedback) {
                const npsSettings = this.getNpsSettings();
                if (this.sessionNumber === 1) {
                    return;
                }
                this.patchNpsSettings({
                    closedSessions: { ...npsSettings.closedSessions, [this.sessionNumber]: true },
                });
                this.sessionsViews[this.sessionNumber] = true;
                return;
            }

            const npsPayload = {
                ...feedback,
                sessionNumber: this.sessionNumber,
            };

            if (fromButton) {
                npsPayload.sessionNumber = 0;
            }

            if (fromAiAssistant) {
                npsPayload.sessionNumber = 4;
            }

            const pageId = isUuid(this.pageId) ? undefined : (this.pageId as number);
            this.npsHttpService.setNpsData(npsPayload, pageId).subscribe((res) => {
                this.ngOnInit();
            });
        });
        return modalRef;
    }

    setPublishTime(date: Date | string): void {
        const npsSettings = this.getNpsSettings();
        if (npsSettings.firstPublishedTime) {
            return;
        }

        this.patchNpsSettings({ firstPublishedTime: new Date(date).getTime() });
    }

    public showNpsModal(fromAiAssistant: boolean) {
        if (fromAiAssistant) {
            this.show(() => {}, false, true);
        } else {
            this.show(() => {}, true, false);
        }
    }
}
